import {useCallback, useEffect, useState} from 'react';

import {useDispatch, useSelector} from 'react-redux';

import {deleteUserCreditCard} from '~/shared/services/bffService/api';
import {ApiError, is401Error, isApiError} from '~/shared/services/apiErrorService';
import {handleRefreshToken} from '~/shared/services/auth';
import {MoneyCard, UserGetPaymentMethodsResponse} from '~/shared/services/bffService/api/interfaces/responses';
import {UserCreditCard, UserPaypal} from '~/personalArea/components/Sections/Payments/types';
import {trackEvent} from '~/shared/services/analytics';
import {useLocation} from '~/shared/router';
import apiService from '~/shared/services/apiService';
import {selectUser} from '~/shared/store/storeModules/user';
import {convertPathnameToAnalyticsPageSource} from '~/shared/services/analytics/utils';
import {createLogger} from '~/shared/logging';
import {selectUserPayments} from '~/shared/store/storeModules/payments/paymentsSelectors';
import {activateUserMoneycardById, getUserPayments} from '~/shared/store/storeModules/payments/paymentsActions';

const logger = createLogger('usePayments');

const combinePaymentsInList = (paymentsData: UserGetPaymentMethodsResponse): (UserCreditCard | UserPaypal)[] => {
  const cards: UserCreditCard[] = paymentsData.creditCards || [];
  const paypals: UserPaypal[] = paymentsData?.paypal?.map(p => ({...p, type: 'paypal'})) || [];

  return [...cards, ...paypals];
};

const usePayments = ({
  autoFetch = true,
}: {
  autoFetch?: boolean;
} = {}) => {
  const dispatch = useDispatch();
  const [payments, setPayments] = useState<(UserCreditCard | UserPaypal)[] | null>(null);
  const [moneyCards, setMoneyCards] = useState<MoneyCard[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const location = useLocation();
  const user = useSelector(selectUser);

  const userPayments = useSelector(selectUserPayments);

  const getUserPaymentsHandler = useCallback(async () => {
    try {
      await dispatch(getUserPayments());
    } catch (err) {
      if (is401Error(err)) {
        await handleRefreshToken(err, getUserPaymentsHandler);
        return;
      }
      if (isApiError(err)) {
        setError(err);
      }
      if (!isApiError(err)) {
        logger.warn('Unexpected error in getPayments');
      }
    }
  }, [dispatch]);

  const removePaymentMethodHandler = useCallback(async (args: {
    id: number;
    type: string;
  }): Promise<boolean> => {
    try {
      const {id, type} = args;
      if (type !== 'paypal') {
        await deleteUserCreditCard(id);
      }
      if (type === 'paypal') {
        await apiService.removePayPalUserToken({paypalId: id});
      }
      trackEvent('removePaymentMethod_successful', {
        linkType: type === 'paypal' ? type : 'credit_card',
        pageSource: convertPathnameToAnalyticsPageSource(location.pathname),
      });
      getUserPaymentsHandler();
      return true;
    } catch (err) {
      if (is401Error(err)) {
        return handleRefreshToken(err, removePaymentMethodHandler, args);
      }

      return false;
    }
  }, [getUserPaymentsHandler, location.pathname]);

  useEffect(() => {
    if (userPayments.data) {
      setPayments(combinePaymentsInList(userPayments.data));
      setMoneyCards(userPayments.data.moneyCards);
    }
  }, [userPayments]);

  useEffect(() => {
    const isEmptyDefaultPayments = !userPayments.loading &&
      !userPayments.loaded &&
      !userPayments.data;
    if (autoFetch && isEmptyDefaultPayments && user.loaded && !user.loading && user.data) {
      getUserPaymentsHandler();
    }
  }, [getUserPaymentsHandler, userPayments, user, autoFetch]);

  const tenbisCreditBalance = moneyCards?.find(card => card.isTenbisCredit)?.balance || 0;

  const activateMoneyCardById = useCallback((id: string) => {
    if (!moneyCards?.length) {
      logger.warn('No moneycards to activate');
      return;
    }

    dispatch(activateUserMoneycardById(id));
  }, [moneyCards, dispatch]);

  return {
    payments,
    moneyCards,
    tenbisCreditBalance,
    isLoaded: userPayments.loaded && user.loaded && !user.loading,
    isLoading: userPayments.loading,
    activateMoneyCardById,
    removePaymentMethodHandler,
    getUserPayments: getUserPaymentsHandler,
    error,
  };
};

export default usePayments;
